import { motion } from "framer-motion";
import { Reveal, SectionHeading } from "./Reveal";
import { ECOSYSTEM_NODES } from "@/data/content";

export default function Ecosystem() {
  return (
    <section
      id="approach"
      className="py-24 sm:py-32 bg-forest relative overflow-hidden"
      data-testid="approach-section"
    >
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: "radial-gradient(circle at 30% 20%, #C6A052 0%, transparent 45%), radial-gradient(circle at 75% 80%, #90A955 0%, transparent 45%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <SectionHeading
          dark
          eyebrow="The Aarogyam Bharat Approach"
          title="We Don't Just Look at the Symptom. We Look at the System."
          sub="Your health is interconnected. The way you eat influences energy. Movement supports metabolic health. Stress can affect sleep and daily habits. Sleep supports recovery. Your environment influences consistency. That's why our coaching looks at the whole system rather than isolating one problem."
        />

        <Reveal className="flex justify-center">
          <div
            className="relative w-[320px] h-[320px] sm:w-[480px] sm:h-[480px] lg:w-[560px] lg:h-[560px]"
            data-testid="ecosystem-diagram"
          >
            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
              <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(198,160,82,0.25)" strokeWidth="0.3" strokeDasharray="1.5 2" />
              {ECOSYSTEM_NODES.map((_, i) => {
                const a = (i / ECOSYSTEM_NODES.length) * 2 * Math.PI - Math.PI / 2;
                const x = 50 + 38 * Math.cos(a);
                const y = 50 + 38 * Math.sin(a);
                return (
                  <line key={i} x1="50" y1="50" x2={x} y2={y} stroke="rgba(144,169,85,0.28)" strokeWidth="0.3" />
                );
              })}
            </svg>

            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 90, ease: "linear" }}
              className="absolute inset-0"
            >
              {ECOSYSTEM_NODES.map((node, i) => {
                const a = (i / ECOSYSTEM_NODES.length) * 2 * Math.PI - Math.PI / 2;
                const x = 50 + 38 * Math.cos(a);
                const y = 50 + 38 * Math.sin(a);
                return (
                  <div
                    key={node}
                    className="absolute"
                    style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
                  >
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ repeat: Infinity, duration: 90, ease: "linear" }}
                    >
                      <motion.span
                        whileHover={{ scale: 1.15 }}
                        className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-ivory/10 backdrop-blur-md border border-moss/30 text-ivory text-[10px] sm:text-xs font-semibold tracking-wide whitespace-nowrap cursor-default"
                        data-testid={`ecosystem-node-${node.toLowerCase()}`}
                      >
                        {node}
                      </motion.span>
                    </motion.div>
                  </div>
                );
              })}
            </motion.div>

            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="w-32 h-32 sm:w-44 sm:h-44 rounded-full bg-gradient-to-br from-sage to-botanical border border-gold/40 flex items-center justify-center shadow-hover"
              >
                <p className="font-serif italic text-xl sm:text-2xl text-ivory text-center leading-tight px-4">
                  Your
                  <br />
                  Health
                </p>
              </motion.div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="text-center mt-14">
          <p className="text-ivory/70 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Your body doesn't need another extreme diet.{" "}
            <span className="text-gold font-serif italic text-lg">It needs the right system.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
